/**
 * API Cache Hook for improved performance through client-side caching
 * 
 * Provides a caching layer for API responses to reduce network requests
 * and improve perceived performance.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { ApiResponse } from '@/types/api';

// Cache entry with expiration
interface CacheEntry<T> {
  data: ApiResponse<T>;
  timestamp: number;
  key: string;
}

// Cache configuration options
export interface CacheOptions {
  /** Time in milliseconds before cache expires (default: 5 minutes) */
  ttl?: number;
  /** Whether to use cache when offline (default: true) */
  useOffline?: boolean;
  /** Whether to invalidate cache on window focus (default: false) */
  invalidateOnFocus?: boolean;
  /** Whether to persist cache in localStorage (default: false) */
  persist?: boolean;
  /** Storage key prefix for persisted cache (default: 'api_cache') */
  storageKeyPrefix?: string;
}

// Default cache options
const DEFAULT_CACHE_OPTIONS: CacheOptions = {
  ttl: 5 * 60 * 1000, // 5 minutes
  useOffline: true,
  invalidateOnFocus: false,
  persist: false,
  storageKeyPrefix: 'api_cache',
};

// Global in-memory cache store
const memoryCache = new Map<string, CacheEntry<unknown>>();

/**
 * Hook for managing API response caching
 * 
 * @param options Cache configuration options
 * @returns Cache management functions
 */
export function useApiCache(options: CacheOptions = {}) {
  const mergedOptions = { ...DEFAULT_CACHE_OPTIONS, ...options };
  const [cacheSize, setCacheSize] = useState<number>(memoryCache.size);
  
  // Reference to track if component is mounted
  const isMounted = useRef(true);
  
  /**
   * Generate a cache key from a request identifier and parameters
   * 
   * @param identifier Unique identifier for the API endpoint
   * @param params Optional parameters that affect the response
   * @returns Cache key string
   */
  const generateCacheKey = useCallback((identifier: string, params?: Record<string, unknown>): string => {
    if (!params) return identifier;
    
    // Sort keys to ensure consistent cache keys regardless of object property order
    const sortedParams = typeof params === 'object' 
      ? Object.keys(params)
          .sort()
          .reduce((result: Record<string, unknown>, key: string) => {
            if (params[key] !== undefined) {
              result[key] = params[key];
            }
            return result;
          }, {})
      : params;
    
    return `${identifier}:${JSON.stringify(sortedParams)}`;
  }, []);
  
  /**
   * Get a value from cache if it exists and is not expired
   * 
   * @param key Cache key
   * @returns Cached data or null if not found or expired
   */
  const getFromCache = useCallback(<T>(key: string): ApiResponse<T> | null => {
    // Check memory cache first
    const memoryEntry = memoryCache.get(key) as CacheEntry<T> | undefined;
    
    if (memoryEntry) {
      const now = Date.now();
      if (now - memoryEntry.timestamp < mergedOptions.ttl!) {
        return memoryEntry.data;
      } else {
        // Remove expired entry
        memoryCache.delete(key);
        if (mergedOptions.persist) {
          localStorage.removeItem(`${mergedOptions.storageKeyPrefix}_${key}`);
        }
        if (isMounted.current) {
          setCacheSize(memoryCache.size);
        }
        return null;
      }
    }
    
    // Check localStorage if persistence is enabled
    if (mergedOptions.persist) {
      try {
        const storedItem = localStorage.getItem(`${mergedOptions.storageKeyPrefix}_${key}`);
        if (storedItem) {
          const parsedItem = JSON.parse(storedItem) as CacheEntry<T>;
          const now = Date.now();
          
          if (now - parsedItem.timestamp < mergedOptions.ttl!) {
            // Restore to memory cache
            memoryCache.set(key, parsedItem);
            if (isMounted.current) {
              setCacheSize(memoryCache.size);
            }
            return parsedItem.data;
          } else {
            // Remove expired entry
            localStorage.removeItem(`${mergedOptions.storageKeyPrefix}_${key}`);
            return null;
          }
        }
      } catch (error) {
        console.error('Error retrieving from cache:', error);
      }
    }
    
    return null;
  }, [mergedOptions.ttl, mergedOptions.persist, mergedOptions.storageKeyPrefix]);
  
  /**
   * Store a value in the cache
   * 
   * @param key Cache key
   * @param data Data to cache
   */
  const setInCache = useCallback(<T>(key: string, data: ApiResponse<T>): void => {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      key,
    };
    
    memoryCache.set(key, entry);
    
    if (mergedOptions.persist) {
      try {
        localStorage.setItem(
          `${mergedOptions.storageKeyPrefix}_${key}`,
          JSON.stringify(entry)
        );
      } catch (error) {
        console.error('Error storing in cache:', error);
      }
    }
    
    if (isMounted.current) {
      setCacheSize(memoryCache.size);
    }
  }, [mergedOptions.persist, mergedOptions.storageKeyPrefix]);
  
  /**
   * Remove a specific entry from the cache
   * 
   * @param key Cache key to invalidate
   */
  const invalidateCache = useCallback((key: string): void => {
    memoryCache.delete(key);
    
    if (mergedOptions.persist) {
      localStorage.removeItem(`${mergedOptions.storageKeyPrefix}_${key}`);
    }
    
    if (isMounted.current) {
      setCacheSize(memoryCache.size);
    }
  }, [mergedOptions.persist, mergedOptions.storageKeyPrefix]);
  
  /**
   * Remove all entries from the cache
   */
  const clearCache = useCallback((): void => {
    memoryCache.clear();
    
    if (mergedOptions.persist) {
      // Only clear items with our prefix
      const prefix = `${mergedOptions.storageKeyPrefix}_`;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          localStorage.removeItem(key);
        }
      }
    }
    
    if (isMounted.current) {
      setCacheSize(0);
    }
  }, [mergedOptions.persist, mergedOptions.storageKeyPrefix]);
  
  /**
   * Invalidate cache entries by prefix
   * 
   * @param prefix Cache key prefix to invalidate
   */
  const invalidateCacheByPrefix = useCallback((prefix: string): void => {
    // Remove from memory cache
    for (const key of memoryCache.keys()) {
      if (key.startsWith(prefix)) {
        memoryCache.delete(key);
      }
    }
    
    // Remove from localStorage if persistence is enabled
    if (mergedOptions.persist) {
      const storagePrefix = `${mergedOptions.storageKeyPrefix}_${prefix}`;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(storagePrefix)) {
          localStorage.removeItem(key);
        }
      }
    }
    
    if (isMounted.current) {
      setCacheSize(memoryCache.size);
    }
  }, [mergedOptions.persist, mergedOptions.storageKeyPrefix]);
  
  // Set up window focus listener for cache invalidation if enabled
  useEffect(() => {
    if (mergedOptions.invalidateOnFocus) {
      const handleFocus = () => {
        clearCache();
      };
      
      window.addEventListener('focus', handleFocus);
      
      return () => {
        window.removeEventListener('focus', handleFocus);
      };
    }
    
    return undefined;
  }, [mergedOptions.invalidateOnFocus, clearCache]);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  
  return {
    generateCacheKey,
    getFromCache,
    setInCache,
    invalidateCache,
    clearCache,
    invalidateCacheByPrefix,
    cacheSize,
  };
}

export default useApiCache;
